/**
 * Shared file-download helper.
 *
 * Six near-copies of this logic already exist (subscriptionService, purchaseOrderService,
 * invoiceService, paymentService, deliveryDetailsService, customerSupportService). All of them
 * share two defects, and both are fixed here rather than copied a seventh time:
 *
 *   1. They never call URL.revokeObjectURL, so every export leaks the blob until the tab closes.
 *      That is cheap for a spreadsheet and expensive for a multi-hundred-megabyte log slice.
 *   2. With responseType:'blob' a FAILED response body is also a Blob, so `e.response.data.message`
 *      is undefined and the toast always shows the generic fallback. The server's real message —
 *      "Raw download is not permitted", "Too many log reads in flight" — never reaches the user.
 *      readBlobError below reads the Blob back as text and pulls the message out.
 */
import axios from 'axios'

/** Turn an error whose body is a Blob back into the server's message. */
function readBlobError(e, fallback, done) {
  var data = e && e.response && e.response.data
  if (!data) {
    done((e && e.message) || fallback)
    return
  }
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) {
    done((data && data.message) || fallback)
    return
  }
  var reader = new FileReader()
  reader.onload = function() {
    try {
      var parsed = JSON.parse(reader.result)
      done(parsed.message || fallback)
    } catch (notJson) {
      done(fallback)
    }
  }
  reader.onerror = function() { done(fallback) }
  reader.readAsText(data)
}

/** Hand the browser a blob to save, then release the object URL. */
export function saveBlob(blob, filename) {
  var url = window.URL.createObjectURL(blob)
  var link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * GET a URL as a file download.
 *
 * @param url      absolute endpoint
 * @param params   query parameters
 * @param filename name offered to the browser
 * @param success  called with no arguments once the file is handed over
 * @param error    called with a human-readable message
 */
export function downloadFile(url, params, filename, success, error) {
  axios.get(url, { params: params, responseType: 'blob' })
    .then(function(r) {
      saveBlob(new Blob([r.data]), filename)
      success()
    })
    .catch(function(e) {
      readBlobError(e, 'Download failed', error)
    })
}

export default { downloadFile: downloadFile, saveBlob: saveBlob }
